import { Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { IFormsProvider, LiveForm, SAVE_FORM_RESULT, UserInformation } from '@lfw/roesberg.liveforms.core';
import { LiveformsEditorComponent } from '@lfw/roesberg.liveforms.editor';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, firstValueFrom, of, take } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { NetworkService, NetworkState } from '../../../network.service';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { CanComponentDeactivate } from '../../../shared/component-based-candeactivate.guard';
import { AppToolbarButtonComponent } from '../../../shared/components/app-toolbar-button/app-toolbar-button.component';
import { AppToolbarDividerComponent } from '../../../shared/components/app-toolbar-divider/app-toolbar-divider.component';
import { AppToolbarTextComponent } from '../../../shared/components/app-toolbar-text/app-toolbar-text.component';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { DefaultDialogService } from '../../../shared/services/default-dialog.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { InfoService } from '../../../shared/services/info.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { MenuService } from '../../../shell/menu.service';
import { UserInformationService } from '../../../shell/user-information.service';
import { FormEditToolbarComponent } from '../../components/form-toolbar/form-edit-toolbar.component';
import { OrderEditorMobileMenuComponent } from '../../components/order-editor-mobile-menu/order-editor-mobile-menu.component';
import { SubStateSelectComponent } from '../../components/sub-state-select/sub-state-select.component';
import { OrdersService } from '../../orders.service';
import { OnlineFormsProvider } from '../online-forms.provider';

@Component({
  selector: 'lfw-online-form-view',
  templateUrl: './online-order-editor.component.html',
  styleUrl: './online-order-editor.component.css',
  providers: [{ provide: IFormsProvider, useClass: OnlineFormsProvider }],
  imports: [
    LiveformsEditorComponent,
    FormEditToolbarComponent,
    AppToolbarButtonComponent,
    AppToolbarDividerComponent,
    AppToolbarTextComponent,
    SubStateSelectComponent,
    TranslatePipe,
    OrderEditorMobileMenuComponent
  ]
})
export class OnlineOrderEditorComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly dialog = inject(MatDialog);
  private readonly userInfoService = inject(UserInformationService);
  private readonly routerService = inject(LfwRouterService);
  private readonly translate = inject(TranslateService);
  private readonly infoService = inject(InfoService);
  private readonly orderService = inject(OrdersService);
  private readonly dialogService = inject(DefaultDialogService);
  private readonly errorHandling = inject(ErrorHandlerService);
  private readonly menuService = inject(MenuService);
  private readonly title = inject(Title);
  private readonly networkService = inject(NetworkService);
  private readonly settingsService = inject(SettingsService);

  form: LiveForm | undefined = undefined;
  user: UserInformation = this.userInfoService.userInformation;
  bypassCanDeactivate = false;

  editor = viewChild.required(LiveformsEditorComponent);

  changedForm = signal(false);
  readonly isClosing = signal(false);
  readonly canFinalize = toSignal(this.networkService.state$.pipe(map((state: NetworkState) => state === 'online')), {
    initialValue: navigator.onLine
  });

  formsProvider = inject(IFormsProvider);

  editorSettings = this.settingsService.getEditorSettings();

  private readonly offlineTooltip = toSignal(
    this.translate.stream('ORDERS.FINISH_TOOLTIP_NOT_POSSIBLE_WHILE_OFFLINE'),
    { initialValue: this.translate.instant('ORDERS.FINISH_TOOLTIP_NOT_POSSIBLE_WHILE_OFFLINE') }
  );

  readonly tooltipForFinalizeButton = computed(() => (this.canFinalize() ? undefined : this.offlineTooltip()));

  unload(event: Event): void {
    event.preventDefault();
    if (!this.form) {
      return;
    }
    // Warning: this is a fire and forget call, there is no guaranty, that this call will succeed and pause the order
    this.orderService.pauseOrder(this.form.project.id, this.form.order.id).pipe(take(1)).subscribe();
  }

  constructor() {
    this.menuService.hideNavigation();
  }

  ngOnDestroy(): void {
    this.menuService.showNavigation();
    window.removeEventListener('beforeunload', this.unload);
  }

  ngOnInit(): void {
    this.loadForm();
  }

  private loadForm(forceEdit = false): void {
    this.formsProvider
      .loadFormForEdit(forceEdit) // for testing, must be false later on
      .pipe(
        take(1),
        tap((form: LiveForm) => {
          this.form = form;
          this.title.setTitle(this.form.order.text);
        }),
        catchError((httpError) => {
          this.handleLoadError(httpError);
          return of(httpError);
        })
      )
      .subscribe();
  }

  error(formId: string): void {
    this.dialogService.formNotFound(formId);
  }

  get caption(): string {
    return this.form?.order.text ?? '';
  }

  get viewVisible(): boolean {
    return true; // this.form != undefined && this.form.id != '';
  }

  canSave(): boolean {
    return this.changedForm();
  }

  save(): void {
    if (!this.form) {
      return;
    }
    this.editor()
      .save()
      .pipe(
        map((success) => {
          if (!success) {
            this.infoService.setStatusTranslate('FORM.DLG_SAVE_FAILED');
          } else {
            this.editor().formGroup.markAsPristine();
            this.changedForm.set(false);
            window.removeEventListener('beforeunload', this.unload);
          }
        }),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          return error;
        }),
        take(1)
      )
      .subscribe();
  }

  finalize(): void {
    if (!this.form) {
      return;
    }
    of(this.form)
      .pipe(
        switchMap(() =>
          this.editor()
            .save(this.translate.instant('DIALOGS.FINALIZE_NOT_POSSIBLE'))
            .pipe(
              filter((result) => result !== SAVE_FORM_RESULT.FAILED),
              tap(() => {
                this.editor().formGroup.markAsPristine();
                this.changedForm.set(false);
              })
            )
        ),
        switchMap((result) => {
          if (!this.form || result !== SAVE_FORM_RESULT.SAVED_ONLINE) {
            this.infoService.setStatusTranslate('DIALOGS.FINALIZE_NOT_POSSIBLE');
            return EMPTY;
          }
          return this.orderService.finishOrder(this.form.project.id, this.form.order.id).pipe(
            tap(() => {
              this.bypassCanDeactivate = true;
              this.routerService.navigateToOrdersRoot();
            })
          );
        }),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          return error;
        }),
        take(1)
      )
      .subscribe();
  }

  async close(): Promise<void> {
    if (this.isClosing()) {
      return;
    }
    this.isClosing.set(true);
    /* Pausing the order is implemented in canDeactivate() in this component */
    const networkState = await firstValueFrom(this.networkService.state$);

    if (networkState === 'offline') {
      // avoid canDeactivate prompt/pausing when navigating to offline orders
      this.bypassCanDeactivate = true;
      this.routerService.navigateToOfflineOrdersOverview();
    } else {
      this.routerService.navigateToOrdersRoot();
    }
  }

  subStateSelectionChanged(): void {
    this.editor().formGroup.markAsDirty();
  }

  formChanged(): void {
    window.addEventListener('beforeunload', this.unload);
    this.changedForm.set(this.editor().formGroup.dirty);
  }

  private handleLoadError(httpError: any): void {
    const errorCode = this.errorHandling.getErrorCode(httpError);
    const message = this.errorHandling.getErrorMessage(httpError);
    const title = this.translate.instant('DIALOGS.WARNING');
    this.dialog
      //TODO: This error code 'FORMOWNLOCK' comes directly from the backend and is currently this string. We should think about this and put such error codes into e.g. a static error class.
      .open(errorCode === 'FORMOWNLOCK' ? ConfirmDialogComponent : ErrorDialogComponent, {
        data: { title, message },
        closeOnNavigation: true,
        disableClose: true
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((force) => {
          if (force) {
            this.loadForm(force);
          } else {
            this.close();
          }
        })
      )
      .subscribe();
  }

  // this is always called, when navigating away from this page
  canDeactivate() {
    if (!this.form) {
      return true;
    }
    if (!this.changedForm()) {
      return this.orderService.pauseOrder(this.form.project.id, this.form.order.id).pipe(
        take(1),
        map(() => true),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          this.isClosing.set(false);
          return of(false);
        })
      );
    }
    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.translate.instant('DIALOGS.UNSAVED_CHANGES_TITLE'),
          message: this.translate.instant('DIALOGS.UNSAVED_CHANGES_MESSAGE')
        }
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!this.form) {
            return of(true);
          }
          if (result === true) {
            return this.orderService.pauseOrder(this.form.project.id, this.form.order.id).pipe(
              map(() => true),
              catchError((error) => {
                this.dialogService.showSaveError(error);
                this.isClosing.set(false);
                return of(false);
              })
            );
          }
          this.isClosing.set(false);
          return of(false);
        }),
        take(1)
      );
  }
}
